import { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  PiggyBank,
  CreditCard,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ArrowUp,
  ArrowDown,
  Minus,
  GitCompare,
  Printer,
  Target,
  Sparkles,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { MetricCard } from '@/components/dashboard/MetricCard';
import { FocoScoreCard } from '@/components/dashboard/FocoScoreCard';
import { RecommendationsCard } from '@/components/dashboard/RecommendationsCard';
import { useYearlySummary } from '@/hooks/useYearlySummary';
import { useGoals } from '@/hooks/useGoals';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useProfile } from '@/hooks/useProfile';
import { 
  formatCurrency, 
  formatPercentage,
  getSavingsTrafficLight,
  getDebtTrafficLight,
  getFixedExpensesTrafficLight,
  getCombinedExpensesTrafficLight,
  getCashFlowTrafficLight,
  generateRecommendations,
  getFocoScoreZone,
  getFocoScoreLabel,
  getFocoScoreInterpretation,
} from '@/lib/foco-calculations';
import { Card } from '@/components/ui/card';
import { BudgetRuleGuide } from '@/components/dashboard/BudgetRuleGuide';
import { PremiumPageGate } from '@/components/subscription/PremiumPageGate';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  AreaChart,
  Area,
} from 'recharts';

// Helper to calculate percentage change
const calcChange = (current: number, previous: number): number | null => {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) return null; // Can't calculate meaningful % from zero
  return ((current - previous) / previous) * 100;
};

// Component for displaying change indicator
function ChangeIndicator({ change, invertColors = false }: { change: number | null; invertColors?: boolean }) {
  if (change === null) return <span className="text-muted-foreground text-xs">-</span>;
  
  const isPositive = change > 0;
  const isNeutral = change === 0;
  
  // For expenses/debt, positive change is bad (invertColors = true)
  const colorClass = isNeutral 
    ? 'text-muted-foreground' 
    : invertColors 
      ? (isPositive ? 'text-destructive' : 'text-success')
      : (isPositive ? 'text-success' : 'text-destructive');
  
  const Icon = isNeutral ? Minus : (isPositive ? ArrowUp : ArrowDown);
  
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium ${colorClass}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(change) > 999 ? '+999%' : `${Math.abs(change).toFixed(1)}%`}
    </span>
  );
}

export default function YearlyDashboard() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const { summary, hasActiveGoals, isLoading } = useYearlySummary(selectedYear);
  const { summary: previousYearSummary } = useYearlySummary(selectedYear - 1);
  const { goals } = useGoals();
  const { profile } = useProfile();
  const currency = profile?.default_currency || 'CRC';
  const activeGoalsWithProgress = goals.filter(
    g => g.status === 'active' && Number(g.target_amount) > 0 && (Number(g.current_amount) / Number(g.target_amount)) >= 0.10
  ).length;

  const handlePreviousYear = () => {
    setSelectedYear(selectedYear - 1);
  };

  const handleNextYear = () => {
    setSelectedYear(selectedYear + 1);
  };

  const handlePrint = () => {
    window.print();
  };

  const recommendations = summary 
    ? generateRecommendations({
        total_income: summary.total_income,
        total_fixed_expenses: summary.total_fixed_expenses,
        total_variable_expenses: summary.total_variable_expenses,
        total_savings: summary.total_savings,
        total_investments: summary.total_investments,
        total_debt_payments: summary.total_debt_payments,
        real_cash_flow: summary.real_cash_flow,
        savings_rate: summary.savings_rate,
        debt_ratio: summary.debt_ratio,
        fixed_expense_ratio: summary.fixed_expense_ratio,
        variable_expense_ratio: summary.variable_expense_ratio,
      }, hasActiveGoals)
    : [];

  // Calculate year-over-year changes
  const changes = {
    income: calcChange(summary?.total_income || 0, previousYearSummary?.total_income || 0),
    fixedExpenses: calcChange(summary?.total_fixed_expenses || 0, previousYearSummary?.total_fixed_expenses || 0),
    variableExpenses: calcChange(summary?.total_variable_expenses || 0, previousYearSummary?.total_variable_expenses || 0),
    savings: calcChange((summary?.total_savings || 0) + (summary?.total_investments || 0), (previousYearSummary?.total_savings || 0) + (previousYearSummary?.total_investments || 0)),
    debt: calcChange(summary?.total_debt_payments || 0, previousYearSummary?.total_debt_payments || 0),
    cashFlow: calcChange(summary?.real_cash_flow || 0, previousYearSummary?.real_cash_flow || 0),
    focoScore: (summary?.foco_score || 0) - (previousYearSummary?.foco_score || 0),
  };

  // Calculate end-of-year projection based on months with data
  const projection = (() => {
    if (!summary?.monthly_breakdown) return null;
    
    const monthsWithData = summary.monthly_breakdown.filter(m => m.income > 0);
    const monthCount = monthsWithData.length;
    
    if (monthCount === 0) return null;
    
    // Calculate averages from months with data
    const avgIncome = monthsWithData.reduce((sum, m) => sum + m.income, 0) / monthCount;
    const avgFixedExpenses = monthsWithData.reduce((sum, m) => sum + m.fixedExpenses, 0) / monthCount;
    const avgVariableExpenses = monthsWithData.reduce((sum, m) => sum + m.variableExpenses, 0) / monthCount;
    const avgSavings = monthsWithData.reduce((sum, m) => sum + m.savings + m.investments, 0) / monthCount;
    const avgDebtPayments = monthsWithData.reduce((sum, m) => sum + m.debtPayments, 0) / monthCount;
    const avgFocoScore = monthsWithData.reduce((sum, m) => sum + m.focoScore, 0) / monthCount;
    
    // Project for 12 months
    const projectedIncome = avgIncome * 12;
    const projectedFixedExpenses = avgFixedExpenses * 12;
    const projectedVariableExpenses = avgVariableExpenses * 12;
    const projectedSavings = avgSavings * 12;
    const projectedDebtPayments = avgDebtPayments * 12;
    const projectedCashFlow = projectedIncome - projectedFixedExpenses - projectedVariableExpenses - projectedSavings - projectedDebtPayments;
    
    // Calculate projected ratios
    const projectedSavingsRate = projectedIncome > 0 ? projectedSavings / projectedIncome : 0;
    const projectedDebtRatio = projectedIncome > 0 ? projectedDebtPayments / projectedIncome : 0;
    
    return {
      monthsWithData: monthCount,
      monthsRemaining: 12 - monthCount,
      isComplete: monthCount === 12,
      avgIncome,
      avgFixedExpenses,
      avgVariableExpenses,
      avgSavings,
      avgDebtPayments,
      avgFocoScore: Math.round(avgFocoScore),
      projectedIncome,
      projectedFixedExpenses,
      projectedVariableExpenses,
      projectedSavings,
      projectedDebtPayments,
      projectedCashFlow,
      projectedSavingsRate,
      projectedDebtRatio,
      // Difference between projected and current
      remainingIncome: projectedIncome - (summary?.total_income || 0),
      remainingSavings: projectedSavings - ((summary?.total_savings || 0) + (summary?.total_investments || 0)),
    };
  })();

  // Calculate goal alerts - compare projections vs goals
  const goalAlerts = (() => {
    if (!projection || !goals?.length) return [];
    
    const alerts: Array<{
      type: 'warning' | 'success';
      goalName: string;
      message: string;
      deficit?: number;
      projectedAmount?: number;
      targetAmount?: number;
    }> = [];
    
    // Filter active goals with deadlines in current year
    const yearEndGoals = goals.filter(g => {
      if (g.status !== 'active') return false;
      if (!g.deadline) return false;
      const deadlineYear = new Date(g.deadline).getFullYear();
      return deadlineYear === selectedYear;
    });
    
    yearEndGoals.forEach(goal => {
      const targetAmount = Number(goal.target_amount);
      const currentAmount = Number(goal.current_amount);
      const remaining = targetAmount - currentAmount;
      
      // Calculate projected savings that could go towards this goal
      // Based on avg monthly savings and remaining months
      const deadlineMonth = new Date(goal.deadline!).getMonth() + 1;
      const currentMonth = now.getMonth() + 1;
      const remainingMonths = Math.max(0, deadlineMonth - currentMonth);
      
      // Project savings based on average
      const projectedAdditional = projection.avgSavings * remainingMonths;
      const projectedTotal = currentAmount + projectedAdditional;
      
      if (projectedTotal >= targetAmount) {
        alerts.push({
          type: 'success',
          goalName: goal.name,
          message: `Al ritmo actual, alcanzarás esta meta ${remainingMonths} meses antes de lo esperado.`,
          projectedAmount: projectedTotal,
          targetAmount,
        });
      } else {
        const deficit = targetAmount - projectedTotal;
        const monthlyDeficit = remainingMonths > 0 ? deficit / remainingMonths : deficit;
        alerts.push({
          type: 'warning',
          goalName: goal.name,
          message: `Necesitas ahorrar ${formatCurrency(monthlyDeficit, currency)} adicionales por mes para alcanzar esta meta.`,
          deficit,
          projectedAmount: projectedTotal,
          targetAmount,
        });
      }
    });
    
    // Check general financial health alerts
    if (projection.projectedSavingsRate < 0.10) {
      alerts.push({
        type: 'warning',
        goalName: 'Tasa de Ahorro',
        message: 'Tu tasa de ahorro proyectada es menor al 10%. Considera reducir gastos variables.',
      });
    }
    
    if (projection.projectedDebtRatio > 0.35) {
      alerts.push({
        type: 'warning',
        goalName: 'Ratio de Deuda',
        message: 'Tu ratio de deuda proyectado supera el 35%. Prioriza el pago de deudas.',
      });
    }
    
    if (projection.projectedCashFlow < 0) {
      alerts.push({
        type: 'warning',
        goalName: 'Flujo de Caja',
        message: 'Proyección de flujo de caja negativo. Revisa tus gastos urgentemente.',
      });
    }
    
    return alerts;
  })();

  // Chart data preparation
  const monthlyChartData = summary?.monthly_breakdown.map(m => ({
    name: m.monthName.slice(0, 3),
    Ingresos: m.income,
    Gastos: m.fixedExpenses + m.variableExpenses,
    'Ahorro + Inv.': m.savings + m.investments,
    Deuda: m.debtPayments,
  })) || [];

  const focoScoreData = summary?.monthly_breakdown.map(m => ({
    name: m.monthName.slice(0, 3),
    'FOCO Score': m.focoScore,
  })) || [];

  const cashFlowData = summary?.monthly_breakdown.map(m => ({
    name: m.monthName.slice(0, 3),
    'Flujo de caja': m.cashFlow,
  })) || [];

  const cumulativeData = summary?.monthly_breakdown.reduce((acc, m, index) => {
    const prev = index > 0 ? acc[index - 1] : { 
      Ingresos: 0, 
      Gastos: 0, 
      'Ahorro acumulado': 0 
    };
    acc.push({
      name: m.monthName.slice(0, 3),
      Ingresos: prev.Ingresos + m.income,
      Gastos: prev.Gastos + m.fixedExpenses + m.variableExpenses + m.debtPayments,
      'Ahorro acumulado': prev['Ahorro acumulado'] + m.savings + m.investments,
    });
    return acc;
  }, [] as Array<{ name: string; Ingresos: number; Gastos: number; 'Ahorro acumulado': number }>) || [];

  // Year-over-year comparison chart data
  const yoyComparisonData = summary?.monthly_breakdown.map((m, index) => {
    const prevMonth = previousYearSummary?.monthly_breakdown[index];
    return {
      name: m.monthName.slice(0, 3),
      [`${selectedYear}`]: m.income,
      [`${selectedYear - 1}`]: prevMonth?.income || 0,
    };
  }) || [];

  const yoySavingsData = summary?.monthly_breakdown.map((m, index) => {
    const prevMonth = previousYearSummary?.monthly_breakdown[index];
    return {
      name: m.monthName.slice(0, 3),
      [`${selectedYear}`]: m.savings + m.investments,
      [`${selectedYear - 1}`]: (prevMonth?.savings || 0) + (prevMonth?.investments || 0),
    };
  }) || [];

  const yoyFocoData = summary?.monthly_breakdown.map((m, index) => {
    const prevMonth = previousYearSummary?.monthly_breakdown[index];
    return {
      name: m.monthName.slice(0, 3),
      [`${selectedYear}`]: m.focoScore,
      [`${selectedYear - 1}`]: prevMonth?.focoScore || 0,
    };
  }) || [];

  return (
    <PremiumPageGate
      feature="yearly_dashboard"
      title="Panel anual"
      description="Vista anual completa, comparativos y tendencias — función Premium."
    >
    <AppLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:gap-4 print:hidden">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold truncate flex items-center gap-2">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                Dashboard Anual
              </h1>
              <p className="text-muted-foreground text-sm sm:text-base">
                Consolidado financiero del año
              </p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Year selector */}
              <div className="flex items-center gap-1 bg-card rounded-lg border p-0.5">
                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 touch-manipulation" onClick={handlePreviousYear}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-2 sm:px-4 py-1 font-medium text-sm sm:text-base min-w-[60px] sm:min-w-[80px] text-center">
                  {selectedYear}
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 touch-manipulation" onClick={handleNextYear}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Print button */}
              <Button onClick={handlePrint} className="hidden sm:flex">
                <Printer className="h-4 w-4 mr-2" />
                Exportar PDF
              </Button>
              <Button onClick={handlePrint} size="icon" className="sm:hidden">
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* FOCO Score Anual with YoY comparison */}
        <div className="space-y-2">
          {previousYearSummary && (
            <p className="text-sm text-muted-foreground px-1">
              {selectedYear - 1}: {previousYearSummary.foco_score} pts
              <span className={`ml-2 ${changes.focoScore >= 0 ? 'text-success' : 'text-destructive'}`}>
                ({changes.focoScore >= 0 ? '+' : ''}{changes.focoScore} pts)
              </span>
            </p>
          )}
          <FocoScoreCard 
            score={summary?.foco_score || 0}
            componentData={summary ? {
              savingsRate: summary.savings_rate,
              expenseRatio: summary.total_income > 0 
                ? (summary.total_fixed_expenses + summary.total_variable_expenses + summary.total_debt_payments) / summary.total_income 
                : 0,
              activeGoalsWithProgress,
              debtRatio: summary.debt_ratio,
              cashFlow: summary.real_cash_flow,
              totalIncome: summary.total_income,
            } : undefined}
          />
        </div>

        {/* Metrics Grid - Yearly Totals with YoY changes */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          <MetricCard
            title="Ingresos Totales"
            value={formatCurrency(summary?.total_income || 0, currency)}
            subtitle={<ChangeIndicator change={changes.income} />}
            icon={<TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />}
            delay={0}
          />
          <MetricCard
            title="Gastos Fijos"
            value={formatCurrency(summary?.total_fixed_expenses || 0, currency)}
            subtitle={<ChangeIndicator change={changes.fixedExpenses} invertColors />}
            icon={<Wallet className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getFixedExpensesTrafficLight(summary.fixed_expense_ratio) : undefined}
            delay={50}
          />
          <MetricCard
            title="Gastos Variables"
            value={formatCurrency(summary?.total_variable_expenses || 0, currency)}
            subtitle={<ChangeIndicator change={changes.variableExpenses} invertColors />}
            icon={<CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getCombinedExpensesTrafficLight(summary.fixed_expense_ratio, summary.variable_expense_ratio) : undefined}
            delay={100}
          />
          <MetricCard
            title="Ahorro + Inversión"
            value={formatCurrency((summary?.total_savings || 0) + (summary?.total_investments || 0), currency)}
            subtitle={<ChangeIndicator change={changes.savings} />}
            icon={<PiggyBank className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getSavingsTrafficLight(summary.savings_rate) : undefined}
            delay={150}
          />
          <MetricCard
            title="Pago Deudas"
            value={formatCurrency(summary?.total_debt_payments || 0, currency)}
            subtitle={<ChangeIndicator change={changes.debt} />}
            icon={<TrendingDown className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getDebtTrafficLight(summary.debt_ratio) : undefined}
            delay={200}
          />
          <MetricCard
            title="Flujo Real Anual"
            value={formatCurrency(summary?.real_cash_flow || 0, currency)}
            subtitle={<ChangeIndicator change={changes.cashFlow} />}
            icon={<ArrowUpRight className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getCashFlowTrafficLight(summary.real_cash_flow) : undefined}
            delay={250}
          />
        </div>

        {/* Budget Rule 50/30/20 Guide - Yearly */}
        {summary && summary.total_income > 0 && (
          <BudgetRuleGuide
            totalIncome={summary.total_income}
            fixedExpenses={summary.total_fixed_expenses}
            variableExpenses={summary.total_variable_expenses}
            savings={summary.total_savings}
            investments={summary.total_investments}
            debtPayments={summary.total_debt_payments}
            currency={currency}
          />
        )}

        {/* Year-End Projection Section */}
        {projection && !projection.isComplete && (
          <Card className="p-4 sm:p-6 bg-gradient-to-br from-accent/50 to-accent/20 border-accent">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-primary" />
              <h3 className="text-base sm:text-lg font-semibold">Proyección de Cierre de Año</h3>
              <span className="ml-auto text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                Basado en {projection.monthsWithData} {projection.monthsWithData === 1 ? 'mes' : 'meses'}
              </span>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Proyección estimada para el año completo basada en el promedio de los meses con datos ingresados.
              Faltan {projection.monthsRemaining} {projection.monthsRemaining === 1 ? 'mes' : 'meses'} por registrar.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              <div className="bg-background/80 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Ingresos Proyectados</p>
                <p className="text-sm font-bold text-success">{formatCurrency(projection.projectedIncome, currency)}</p>
                <p className="text-[10px] text-muted-foreground">Prom. mensual: {formatCurrency(projection.avgIncome, currency)}</p>
              </div>
              <div className="bg-background/80 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Gastos Fijos Proy.</p>
                <p className="text-sm font-bold">{formatCurrency(projection.projectedFixedExpenses, currency)}</p>
                <p className="text-[10px] text-muted-foreground">Prom. mensual: {formatCurrency(projection.avgFixedExpenses, currency)}</p>
              </div>
              <div className="bg-background/80 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Gastos Var. Proy.</p>
                <p className="text-sm font-bold">{formatCurrency(projection.projectedVariableExpenses, currency)}</p>
                <p className="text-[10px] text-muted-foreground">Prom. mensual: {formatCurrency(projection.avgVariableExpenses, currency)}</p>
              </div>
              <div className="bg-background/80 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Ahorro Proyectado</p>
                <p className="text-sm font-bold text-primary">{formatCurrency(projection.projectedSavings, currency)}</p>
                <p className="text-[10px] text-muted-foreground">Tasa: {formatPercentage(projection.projectedSavingsRate)}</p>
              </div>
              <div className="bg-background/80 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Deuda Proyectada</p>
                <p className="text-sm font-bold">{formatCurrency(projection.projectedDebtPayments, currency)}</p>
                <p className="text-[10px] text-muted-foreground">Ratio: {formatPercentage(projection.projectedDebtRatio)}</p>
              </div>
              <div className="bg-background/80 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">FOCO Prom. Esperado</p>
                <p className={`text-sm font-bold ${
                  projection.avgFocoScore >= 80 ? 'text-success' :
                  projection.avgFocoScore >= 60 ? 'text-warning' :
                  projection.avgFocoScore >= 40 ? 'text-warning' :
                  'text-destructive'
                }`}>{projection.avgFocoScore} pts</p>
                <p className="text-[10px] text-muted-foreground">Basado en tendencia</p>
              </div>
            </div>

            {/* Progress bars showing actual vs projected */}
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Ingresos: {formatCurrency(summary?.total_income || 0, currency)} de {formatCurrency(projection.projectedIncome, currency)}</span>
                  <span>{Math.round(((summary?.total_income || 0) / projection.projectedIncome) * 100)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-success rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(100, ((summary?.total_income || 0) / projection.projectedIncome) * 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Ahorro: {formatCurrency((summary?.total_savings || 0) + (summary?.total_investments || 0), currency)} de {formatCurrency(projection.projectedSavings, currency)}</span>
                  <span>{Math.round((((summary?.total_savings || 0) + (summary?.total_investments || 0)) / projection.projectedSavings) * 100)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(100, (((summary?.total_savings || 0) + (summary?.total_investments || 0)) / projection.projectedSavings) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Smart Goal Alerts Section */}
        {goalAlerts.length > 0 && (
          <div className="space-y-3 print:hidden">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <h3 className="text-base sm:text-lg font-semibold">Alertas de Metas</h3>
            </div>
            {goalAlerts.map((alert, index) => (
              <Alert 
                key={index} 
                variant={alert.type === 'warning' ? 'destructive' : 'default'}
                className={alert.type === 'success' ? 'border-success bg-success/10' : ''}
              >
                {alert.type === 'warning' ? (
                  <AlertTriangle className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-success" />
                )}
                <AlertTitle className={alert.type === 'success' ? 'text-success' : ''}>
                  {alert.goalName}
                </AlertTitle>
                <AlertDescription className={alert.type === 'success' ? 'text-success/80' : ''}>
                  {alert.message}
                  {alert.projectedAmount && alert.targetAmount && (
                    <span className="block mt-1 text-xs">
                      Proyectado: {formatCurrency(alert.projectedAmount, currency)} / Meta: {formatCurrency(alert.targetAmount, currency)}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {/* Year-over-Year Comparison Section */}
        <Card className="p-4 sm:p-6 border-2 border-dashed border-primary/30">
          <div className="flex items-center gap-2 mb-4">
            <GitCompare className="h-5 w-5 text-primary" />
            <h3 className="text-base sm:text-lg font-semibold">Comparación {selectedYear} vs {selectedYear - 1}</h3>
          </div>
          
          {/* Summary comparison cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Ingresos</p>
              <p className="text-sm font-semibold">{formatCurrency(previousYearSummary?.total_income || 0, currency)}</p>
              <p className="text-xs text-muted-foreground">{selectedYear - 1}</p>
            </div>
            <div className="bg-primary/10 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Ingresos</p>
              <p className="text-sm font-semibold text-primary">{formatCurrency(summary?.total_income || 0, currency)}</p>
              <p className="text-xs text-muted-foreground">{selectedYear}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Ahorro</p>
              <p className="text-sm font-semibold">{formatCurrency((previousYearSummary?.total_savings || 0) + (previousYearSummary?.total_investments || 0), currency)}</p>
              <p className="text-xs text-muted-foreground">{selectedYear - 1}</p>
            </div>
            <div className="bg-primary/10 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Ahorro</p>
              <p className="text-sm font-semibold text-primary">{formatCurrency((summary?.total_savings || 0) + (summary?.total_investments || 0), currency)}</p>
              <p className="text-xs text-muted-foreground">{selectedYear}</p>
            </div>
          </div>

          {/* YoY Income Comparison Chart */}
          <div className="h-48 sm:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yoyComparisonData}>
                <CartesianGrid strokeDasharray="4 4" stroke="hsl(220, 13%, 91%)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(213, 15%, 52%)' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(213, 15%, 52%)' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value, currency)}
                  contentStyle={{ backgroundColor: 'hsl(220, 30%, 98%)', border: '1px solid hsl(220, 13%, 91%)', borderRadius: '10px', boxShadow: '0 4px 20px -4px rgba(27,42,74,0.12)', fontSize: '13px' }}
                />
                <Legend />
                <Bar dataKey={`${selectedYear - 1}`} fill="hsl(213, 20%, 75%)" radius={[6, 6, 0, 0]} />
                <Bar dataKey={`${selectedYear}`} fill="hsl(204, 64%, 47%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* YoY FOCO Score and Savings Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold mb-4">FOCO Score: {selectedYear} vs {selectedYear - 1}</h3>
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={yoyFocoData}>
                  <CartesianGrid strokeDasharray="4 4" stroke="hsl(220, 13%, 91%)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(213, 15%, 52%)' }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: 'hsl(213, 15%, 52%)' }} axisLine={false} tickLine={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'hsl(220, 30%, 98%)', border: '1px solid hsl(220, 13%, 91%)', borderRadius: '10px', boxShadow: '0 4px 20px -4px rgba(27,42,74,0.12)', fontSize: '13px' }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey={`${selectedYear - 1}`}
                    stroke="hsl(213, 20%, 75%)" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: 'hsl(213, 20%, 75%)', strokeWidth: 1, r: 3 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey={`${selectedYear}`}
                    stroke="hsl(204, 64%, 47%)" 
                    strokeWidth={3}
                    dot={{ fill: 'hsl(204, 64%, 47%)', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Ahorro: {selectedYear} vs {selectedYear - 1}</h3>
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={yoySavingsData}>
                  <CartesianGrid strokeDasharray="4 4" stroke="hsl(220, 13%, 91%)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(213, 15%, 52%)' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(213, 15%, 52%)' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                  <Tooltip 
                    formatter={(value: number) => formatCurrency(value, currency)}
                    contentStyle={{ backgroundColor: 'hsl(220, 30%, 98%)', border: '1px solid hsl(220, 13%, 91%)', borderRadius: '10px', boxShadow: '0 4px 20px -4px rgba(27,42,74,0.12)', fontSize: '13px' }}
                  />
                  <Legend />
                  <Bar dataKey={`${selectedYear - 1}`} fill="hsl(145, 63%, 49%, 0.35)" radius={[6, 6, 0, 0]} />
                  <Bar dataKey={`${selectedYear}`} fill="hsl(145, 63%, 49%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Monthly Comparison Chart */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Ingresos vs Gastos por Mes ({selectedYear})</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="4 4" stroke="hsl(220, 13%, 91%)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'hsl(213, 15%, 52%)' }} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: 'hsl(213, 15%, 52%)' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value, currency)}
                  contentStyle={{ backgroundColor: 'hsl(220, 30%, 98%)', border: '1px solid hsl(220, 13%, 91%)', borderRadius: '10px', boxShadow: '0 4px 20px -4px rgba(27,42,74,0.12)', fontSize: '13px' }}
                />
                <Legend />
                <Bar dataKey="Ingresos" fill="hsl(204, 64%, 47%)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Gastos" fill="hsl(218, 45%, 20%)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Ahorro + Inv." fill="hsl(145, 63%, 49%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* FOCO Score Trend and Cash Flow */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Evolución del FOCO Score ({selectedYear})</h3>
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={focoScoreData}>
                  <CartesianGrid strokeDasharray="4 4" stroke="hsl(220, 13%, 91%)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'hsl(213, 15%, 52%)' }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: 'hsl(213, 15%, 52%)' }} axisLine={false} tickLine={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'hsl(220, 30%, 98%)', border: '1px solid hsl(220, 13%, 91%)', borderRadius: '10px', boxShadow: '0 4px 20px -4px rgba(27,42,74,0.12)', fontSize: '13px' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="FOCO Score" 
                    stroke="hsl(204, 64%, 47%)" 
                    strokeWidth={3}
                    dot={{ fill: 'hsl(204, 64%, 47%)', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Flujo de Caja Mensual ({selectedYear})</h3>
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cashFlowData}>
                  <CartesianGrid strokeDasharray="4 4" stroke="hsl(220, 13%, 91%)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'hsl(213, 15%, 52%)' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: 'hsl(213, 15%, 52%)' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                  <Tooltip 
                    formatter={(value: number) => formatCurrency(value, currency)}
                    contentStyle={{ backgroundColor: 'hsl(220, 30%, 98%)', border: '1px solid hsl(220, 13%, 91%)', borderRadius: '10px', boxShadow: '0 4px 20px -4px rgba(27,42,74,0.12)', fontSize: '13px' }}
                  />
                  <Bar 
                    dataKey="Flujo de caja" 
                    fill="hsl(204, 64%, 47%)"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Cumulative Progress */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Progreso Acumulado del Año ({selectedYear})</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cumulativeData}>
                <CartesianGrid strokeDasharray="4 4" stroke="hsl(220, 13%, 91%)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'hsl(213, 15%, 52%)' }} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: 'hsl(213, 15%, 52%)' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value, currency)}
                  contentStyle={{ backgroundColor: 'hsl(220, 30%, 98%)', border: '1px solid hsl(220, 13%, 91%)', borderRadius: '10px', boxShadow: '0 4px 20px -4px rgba(27,42,74,0.12)', fontSize: '13px' }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="Ingresos" 
                  stackId="1"
                  stroke="hsl(204, 64%, 47%)" 
                  fill="hsl(204, 64%, 47%, 0.2)"
                />
                <Area 
                  type="monotone" 
                  dataKey="Ahorro acumulado" 
                  stackId="2"
                  stroke="hsl(145, 63%, 49%)" 
                  fill="hsl(145, 63%, 49%, 0.2)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Monthly Breakdown Table */}
        <Card className="p-4 sm:p-6 overflow-x-auto">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Desglose Mensual ({selectedYear})</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-2 font-medium">Mes</th>
                <th className="text-right py-2 px-2 font-medium">Ingresos</th>
                <th className="text-right py-2 px-2 font-medium hidden sm:table-cell">G. Fijos</th>
                <th className="text-right py-2 px-2 font-medium hidden sm:table-cell">G. Var.</th>
                <th className="text-right py-2 px-2 font-medium">Ahorro</th>
                <th className="text-right py-2 px-2 font-medium">FOCO</th>
              </tr>
            </thead>
            <tbody>
              {summary?.monthly_breakdown.map((m) => (
                <tr key={m.month} className="border-b border-muted/50 hover:bg-muted/30">
                  <td className="py-2 px-2 font-medium">{m.monthName.slice(0, 3)}</td>
                  <td className="text-right py-2 px-2 text-success">
                    {m.income > 0 ? formatCurrency(m.income, currency) : '-'}
                  </td>
                  <td className="text-right py-2 px-2 hidden sm:table-cell">
                    {m.fixedExpenses > 0 ? formatCurrency(m.fixedExpenses, currency) : '-'}
                  </td>
                  <td className="text-right py-2 px-2 hidden sm:table-cell">
                    {m.variableExpenses > 0 ? formatCurrency(m.variableExpenses, currency) : '-'}
                  </td>
                  <td className="text-right py-2 px-2 text-primary">
                    {(m.savings + m.investments) > 0 ? formatCurrency(m.savings + m.investments, currency) : '-'}
                  </td>
                  <td className="text-right py-2 px-2">
                    <span className={`font-semibold ${
                      m.focoScore >= 80 ? 'text-success' :
                      m.focoScore >= 60 ? 'text-warning' :
                      m.focoScore >= 40 ? 'text-warning' :
                      'text-destructive'
                    }`}>
                      {m.income > 0 ? m.focoScore : '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold bg-muted/50">
                <td className="py-2 px-2">Total</td>
                  <td className="text-right py-2 px-2 text-success">
                  {formatCurrency(summary?.total_income || 0, currency)}
                </td>
                <td className="text-right py-2 px-2 hidden sm:table-cell">
                  {formatCurrency(summary?.total_fixed_expenses || 0, currency)}
                </td>
                <td className="text-right py-2 px-2 hidden sm:table-cell">
                  {formatCurrency(summary?.total_variable_expenses || 0, currency)}
                </td>
                <td className="text-right py-2 px-2 text-primary">
                  {formatCurrency((summary?.total_savings || 0) + (summary?.total_investments || 0), currency)}
                </td>
                <td className="text-right py-2 px-2">
                  <span className="text-primary font-bold">{summary?.foco_score || 0}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </Card>

        {/* Recommendations */}
        <RecommendationsCard recommendations={recommendations} />

        {/* Printable Report - Hidden on screen, visible only when printing */}
        <div className="hidden print:block print-report">
          {/* Report Header */}
          <div className="text-center mb-8 pb-4 border-b-2 border-primary">
            <h1 className="text-2xl font-bold text-primary mb-1">Tu Dinero, Tu Poder</h1>
            <p className="text-lg font-semibold">FOCO Dashboard Pro</p>
            <p className="text-sm text-gray-600 mt-2">Reporte Anual {selectedYear}</p>
            <p className="text-sm text-gray-500">Cliente: {profile?.name || "Usuario"}</p>
            <p className="text-xs text-gray-400 mt-1">
              Generado el {new Date().toLocaleDateString("es-CR", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>

          {/* FOCO Score Section */}
          <div className="mb-6 p-4 bg-primary/10 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold">FOCO Score Anual</h2>
                <p className="text-sm">{getFocoScoreLabel(getFocoScoreZone(summary?.foco_score || 0))}</p>
              </div>
              <span className="text-4xl font-bold text-primary">{summary?.foco_score || 0}</span>
            </div>
            <p className="text-sm mt-2 italic">{getFocoScoreInterpretation(summary?.foco_score || 0)}</p>
            
            {previousYearSummary && (
              <div className="mt-3 pt-3 border-t border-gray-300">
                <p className="text-sm">
                  <strong>Comparación con {selectedYear - 1}:</strong> {previousYearSummary.foco_score} pts 
                  <span className={changes.focoScore >= 0 ? "text-green-600" : "text-red-500"}>
                    {" "}({changes.focoScore >= 0 ? "+" : ""}{changes.focoScore} puntos)
                  </span>
                </p>
              </div>
            )}
          </div>

          {/* Financial Summary */}
          <div className="mb-6">
            <h2 className="text-lg font-bold mb-3 pb-1 border-b">Resumen Financiero Anual</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Ingresos Totales:</span>
                  <span className="font-semibold text-green-600">{formatCurrency(summary?.total_income || 0, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Gastos Fijos:</span>
                  <span className="font-semibold">{formatCurrency(summary?.total_fixed_expenses || 0, currency)} ({formatPercentage(summary?.fixed_expense_ratio || 0)})</span>
                </div>
                <div className="flex justify-between">
                  <span>Gastos Variables:</span>
                  <span className="font-semibold">{formatCurrency(summary?.total_variable_expenses || 0, currency)} ({formatPercentage(summary?.variable_expense_ratio || 0)})</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Ahorro + Inversión:</span>
                  <span className="font-semibold text-primary">{formatCurrency((summary?.total_savings || 0) + (summary?.total_investments || 0), currency)} ({formatPercentage(summary?.savings_rate || 0)})</span>
                </div>
                <div className="flex justify-between">
                  <span>Pago de Deudas:</span>
                  <span className="font-semibold">{formatCurrency(summary?.total_debt_payments || 0, currency)} ({formatPercentage(summary?.debt_ratio || 0)})</span>
                </div>
                <div className="flex justify-between">
                  <span>Flujo de Caja Real:</span>
                  <span className={`font-semibold ${(summary?.real_cash_flow || 0) >= 0 ? "text-green-600" : "text-red-500"}`}>
                    {formatCurrency(summary?.real_cash_flow || 0, currency)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Year-End Projection for Print */}
          {projection && !projection.isComplete && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-primary">
              <h2 className="text-lg font-bold mb-3">Proyección de Cierre de Año</h2>
              <p className="text-sm mb-3">
                Estimaciones basadas en {projection.monthsWithData} {projection.monthsWithData === 1 ? 'mes' : 'meses'} de datos. 
                Faltan {projection.monthsRemaining} {projection.monthsRemaining === 1 ? 'mes' : 'meses'} por registrar.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Ingresos Proyectados:</span>
                    <span className="font-semibold text-green-600">{formatCurrency(projection.projectedIncome, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Gastos Fijos Proy.:</span>
                    <span className="font-semibold">{formatCurrency(projection.projectedFixedExpenses, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Gastos Variables Proy.:</span>
                    <span className="font-semibold">{formatCurrency(projection.projectedVariableExpenses, currency)}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Ahorro Proyectado:</span>
                    <span className="font-semibold text-primary">{formatCurrency(projection.projectedSavings, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tasa de Ahorro Proy.:</span>
                    <span className="font-semibold">{formatPercentage(projection.projectedSavingsRate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>FOCO Score Esperado:</span>
                    <span className="font-semibold">{projection.avgFocoScore} pts</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Year-over-Year Comparison */}
          {previousYearSummary && (
            <div className="mb-6">
              <h2 className="text-lg font-bold mb-3 pb-1 border-b">Comparación Año vs Año ({selectedYear} vs {selectedYear - 1})</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-2">Métrica</th>
                    <th className="text-right p-2">{selectedYear - 1}</th>
                    <th className="text-right p-2">{selectedYear}</th>
                    <th className="text-right p-2">Cambio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-2">Ingresos</td>
                    <td className="text-right p-2">{formatCurrency(previousYearSummary.total_income, currency)}</td>
                    <td className="text-right p-2">{formatCurrency(summary?.total_income || 0, currency)}</td>
                    <td className={`text-right p-2 ${(changes.income || 0) >= 0 ? "text-green-600" : "text-red-500"}`}>
                      {changes.income !== null ? `${changes.income >= 0 ? "+" : ""}${changes.income.toFixed(1)}%` : "-"}
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="p-2">Gastos Totales</td>
                    <td className="text-right p-2">{formatCurrency(previousYearSummary.total_fixed_expenses + previousYearSummary.total_variable_expenses, currency)}</td>
                    <td className="text-right p-2">{formatCurrency((summary?.total_fixed_expenses || 0) + (summary?.total_variable_expenses || 0), currency)}</td>
                    <td className={`text-right p-2 ${((changes.fixedExpenses || 0) + (changes.variableExpenses || 0)) / 2 <= 0 ? "text-green-600" : "text-red-500"}`}>
                      {changes.fixedExpenses !== null ? `${((changes.fixedExpenses || 0) + (changes.variableExpenses || 0)) / 2 >= 0 ? "+" : ""}${(((changes.fixedExpenses || 0) + (changes.variableExpenses || 0)) / 2).toFixed(1)}%` : "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="p-2">Ahorro + Inversión</td>
                    <td className="text-right p-2">{formatCurrency(previousYearSummary.total_savings + previousYearSummary.total_investments, currency)}</td>
                    <td className="text-right p-2">{formatCurrency((summary?.total_savings || 0) + (summary?.total_investments || 0), currency)}</td>
                    <td className={`text-right p-2 ${(changes.savings || 0) >= 0 ? "text-green-600" : "text-red-500"}`}>
                      {changes.savings !== null ? `${changes.savings >= 0 ? "+" : ""}${changes.savings.toFixed(1)}%` : "-"}
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="p-2">FOCO Score</td>
                    <td className="text-right p-2">{previousYearSummary.foco_score}</td>
                    <td className="text-right p-2">{summary?.foco_score || 0}</td>
                    <td className={`text-right p-2 ${changes.focoScore >= 0 ? "text-green-600" : "text-red-500"}`}>
                      {changes.focoScore >= 0 ? "+" : ""}{changes.focoScore} pts
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Monthly Breakdown */}
          <div className="mb-6">
            <h2 className="text-lg font-bold mb-3 pb-1 border-b">Desglose Mensual {selectedYear}</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-2">Mes</th>
                  <th className="text-right p-2">Ingresos</th>
                  <th className="text-right p-2">G. Fijos</th>
                  <th className="text-right p-2">G. Variables</th>
                  <th className="text-right p-2">Ahorro + Inv.</th>
                  <th className="text-right p-2">Deuda</th>
                  <th className="text-right p-2">FOCO</th>
                </tr>
              </thead>
              <tbody>
                {summary?.monthly_breakdown.map((m, index) => (
                  <tr key={m.month} className={index % 2 === 0 ? "" : "bg-gray-50"}>
                    <td className="p-2 font-medium">{m.monthName}</td>
                    <td className="text-right p-2 text-green-600">{m.income > 0 ? formatCurrency(m.income, currency) : "-"}</td>
                    <td className="text-right p-2">{m.fixedExpenses > 0 ? formatCurrency(m.fixedExpenses, currency) : "-"}</td>
                    <td className="text-right p-2">{m.variableExpenses > 0 ? formatCurrency(m.variableExpenses, currency) : "-"}</td>
                    <td className="text-right p-2 text-primary">{(m.savings + m.investments) > 0 ? formatCurrency(m.savings + m.investments, currency) : "-"}</td>
                    <td className="text-right p-2">{m.debtPayments > 0 ? formatCurrency(m.debtPayments, currency) : "-"}</td>
                    <td className="text-right p-2 font-semibold">{m.income > 0 ? m.focoScore : "-"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold bg-gray-200">
                  <td className="p-2">TOTAL</td>
                  <td className="text-right p-2 text-green-600">{formatCurrency(summary?.total_income || 0, currency)}</td>
                  <td className="text-right p-2">{formatCurrency(summary?.total_fixed_expenses || 0, currency)}</td>
                  <td className="text-right p-2">{formatCurrency(summary?.total_variable_expenses || 0, currency)}</td>
                  <td className="text-right p-2 text-primary">{formatCurrency((summary?.total_savings || 0) + (summary?.total_investments || 0), currency)}</td>
                  <td className="text-right p-2">{formatCurrency(summary?.total_debt_payments || 0, currency)}</td>
                  <td className="text-right p-2">{summary?.foco_score || 0}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold mb-3 pb-1 border-b">Recomendaciones para el Próximo Año</h2>
              <ul className="list-disc list-inside space-y-1">
                {recommendations.map((rec, index) => (
                  <li key={index} className="text-sm">{rec}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-4 border-t text-center text-xs text-gray-500">
            <p>Tu Dinero, Tu Poder — FOCO Dashboard Pro</p>
            <p className="mt-1">Este reporte es confidencial y para uso personal del cliente.</p>
          </div>
        </div>
      </div>
    </AppLayout>
    </PremiumPageGate>
  );
}
